/**
 * AEO Priorities Tab Component
 *
 * Main component for displaying AEO (Answer Engine Optimization) priorities.
 * Provides three levels of prioritization: URL, Product, and Category.
 * Includes exclusion management for filtering non-relevant URLs.
 *
 * Part of Bot Category Analytics & AEO Prioritization Engine
 *
 * Date: January 16, 2026
 */

import { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Target, RefreshCw, AlertTriangle, TrendingUp, Package, FolderTree,
  Filter, Download, Sparkles, Bot, Percent, BarChart3, Settings
} from 'lucide-react';

import { UrlPrioritiesTable } from './UrlPrioritiesTable';
import { ProductPrioritiesTable } from './ProductPrioritiesTable';
import { CategoryPrioritiesTable } from './CategoryPrioritiesTable';
import { ExclusionManager } from './ExclusionManager';
import { SitemapConfigDialog } from './SitemapConfigDialog';

import {
  calculatePriorities,
  getPrioritySummary,
  getImpactProjection,
  ImpactProjection,
} from '@/services/aeoPrioritiesService';

interface Summary {
  total_urls: number;
  total_products: number;
  total_categories: number;
  avg_url_score: number;
  avg_product_score: number;
  avg_category_score: number;
  top_category: string;
  calculation_date: string;
}

export function AeoPrioritiesTab() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSitemapError, setIsSitemapError] = useState(false);
  const [sitemapDialogOpen, setSitemapDialogOpen] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [impact, setImpact] = useState<ImpactProjection | null>(null);
  const [activeSubtab, setActiveSubtab] = useState('urls');

  const siteId = selectedSite?.id;

  useEffect(() => {
    if (siteId) {
      loadData();
    }
  }, [siteId]);

  const loadData = async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, impactRes] = await Promise.all([
        getPrioritySummary(siteId),
        getImpactProjection(siteId, 50, 'url'),
      ]);
      setSummary(summaryRes.summary);
      setImpact(impactRes);
    } catch (err) {
      console.error('Failed to load AEO priorities data:', err);
      // Don't show error if just no data yet
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!siteId) return;
    setCalculating(true);
    setError(null);
    setIsSitemapError(false);
    try {
      const result = await calculatePriorities(siteId);
      if (result.success) {
        await loadData();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to calculate priorities';
      setError(errorMessage);
      // Check if this is a sitemap-related error
      const isSitemap = errorMessage.toLowerCase().includes('sitemap') ||
                        errorMessage.toLowerCase().includes('no urls');
      setIsSitemapError(isSitemap);
      console.error(err);
    } finally {
      setCalculating(false);
    }
  };

  const handleSitemapConfigSuccess = () => {
    // After sitemap is configured, trigger a recalculation
    handleCalculate();
  };

  if (!siteId) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Please select a site to view AEO priorities.</AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-orange-500" />
            AEO Prioritization Engine
          </h2>
          <p className="text-muted-foreground mt-1">
            Identify which URLs, products, and categories to enrich first for maximum AI visibility impact
          </p>
        </div>
        <Button
          onClick={handleCalculate}
          disabled={calculating}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${calculating ? 'animate-spin' : ''}`} />
          {calculating ? 'Calculating...' : 'Recalculate Priorities'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between w-full">
            <span>{error}</span>
            {isSitemapError && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSitemapDialogOpen(true)}
                className="ml-4"
              >
                <Settings className="h-4 w-4 mr-2" />
                Configure Sitemap
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Sitemap Configuration Dialog */}
      <SitemapConfigDialog
        open={sitemapDialogOpen}
        onOpenChange={setSitemapDialogOpen}
        siteId={siteId}
        onSuccess={handleSitemapConfigSuccess}
      />

      {/* Summary Cards */}
      {summary ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                URLs Analyzed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total_urls?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Avg Score: {summary.avg_url_score ? Number(summary.avg_url_score).toFixed(1) : '0'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Package className="h-4 w-4" />
                Products Identified
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total_products?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Avg Score: {summary.avg_product_score ? Number(summary.avg_product_score).toFixed(1) : '0'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FolderTree className="h-4 w-4" />
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total_categories?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Top: {summary.top_category || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {impact && (
            <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-orange-700 dark:text-orange-400 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Impact of Top 50
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                  {impact.projectedImpact.estimatedVisibilityIncrease}
                </div>
                <p className="text-xs text-orange-600 dark:text-orange-500 mt-1">
                  {impact.projectedImpact.aiVisitsCaptured.toLocaleString()} AI visits captured
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No priority data available yet. Click "Recalculate Priorities" to generate scores.
          </AlertDescription>
        </Alert>
      )}

      {/* Subtabs */}
      <Tabs value={activeSubtab} onValueChange={setActiveSubtab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="urls" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            URL Priorities
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Product Priorities
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            Category Priorities
          </TabsTrigger>
          <TabsTrigger value="exclusions" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Exclusions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="urls" className="mt-4">
          <UrlPrioritiesTable siteId={siteId} />
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          <ProductPrioritiesTable siteId={siteId} />
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <CategoryPrioritiesTable siteId={siteId} />
        </TabsContent>

        <TabsContent value="exclusions" className="mt-4">
          <ExclusionManager siteId={siteId} onRulesChanged={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
